// Body checks — sprint-on-carrier contact that stuns the victim and pops
// the puck loose. Arcade-feel mechanic, not a penalty (Phase H decision:
// no penalties).
//
// Owns:   the rule that turns body-on-body contact between a sprinting
//         attacker and a puck-carrying victim into a stun + puck-pop.
// Reads:  input.sprint, input.buttons (BigHit), state.players (positions
//         + velocities + actions), tuning.check, tuning.player.radius.
// Mutates: victim — `hasPuck=false`, `possessionQuality=0`, `action="Stunned"`,
//         `stunTicks=...`, knockback vel.
//         attacker — `action="RecoverCheck"`, `recoveryTicks=...`.
//         state.puck — mode=Loose, ownerId=null, lastTouchedBy=attacker.id,
//         vel popped along the contact normal, noRecaptureUntilTick set.
// Emits:  CheckLanded.
//
// Trigger conditions (all must hold):
//   - two opposing skaters within 2 * player.radius of each other,
//   - the attacker's input.sprint >= bodyCheckSprintThreshold (0.5),
//   - the victim has the puck,
//   - closing speed along the contact normal > bodyCheckMinClosingSpeed,
//   - neither skater is already Stunned or RecoverCheck.
//
// `BigHit` button multiplies stun, knockback, puck-pop, and attacker
// recovery by `bigHitMultiplier` (1.5).
//
// Called from `core/SimTick.ts` Step 9 BEFORE player-vs-player soft
// separation (Step 10) so the overlap signal isn't lost. Zero-alloc.

import { InputButton, type PlayerInput } from "@arcade-hockey/shared";
import { tuning } from "../state/tuning.js";
import type { MatchState, PlayerState } from "../state/types.js";
import { findActor } from "./seat-control.js";

type AttackerState = {
  sprint: number;
  bigHit: boolean;
};

export function applyBodyChecks(state: MatchState, inputs: ReadonlyArray<PlayerInput>): void {
  const attackerStateByPlayerId = new Map<string, AttackerState>();
  for (const input of inputs) {
    // Phase G push 2: use `findActor` (prefers input.playerId) so each
    // bot's sprint/BigHit attaches to that bot's player ID, not the
    // human seat. Previously bots could not deliver body checks because
    // their inputs all resolved to the human-controlled seat.
    const player = findActor(state, input);
    if (player === null) continue;
    attackerStateByPlayerId.set(player.id, {
      sprint: input.sprint,
      bigHit: (input.buttons & InputButton.BigHit) !== 0,
    });
  }

  const threshold = 2 * tuning.player.radius;
  // `state.players` is in canonical sorted order (see createMatch); the pair
  // iteration below visits each unordered pair once.
  const players = state.players;

  for (let i = 0; i < players.length; i++) {
    const a = players[i];
    if (a === undefined) continue;
    for (let j = i + 1; j < players.length; j++) {
      const b = players[j];
      if (b === undefined) continue;
      if (a.teamId === b.teamId) continue;
      if (isDisabled(a) || isDisabled(b)) continue;

      const dx = b.pos.x - a.pos.x;
      const dy = b.pos.y - a.pos.y;
      const dist = Math.sqrt(dx * dx + dy * dy);
      if (dist >= threshold) continue;
      if (dist < 1e-6) continue;

      const resolution = resolveCheckCandidates(a, b, attackerStateByPlayerId);
      if (resolution === null) continue;

      const { attacker, victim } = resolution;
      const normalX = (victim.pos.x - attacker.pos.x) / dist;
      const normalY = (victim.pos.y - attacker.pos.y) / dist;
      const closingSpeed = attacker.vel.x * normalX + attacker.vel.y * normalY;
      if (closingSpeed < tuning.check.bodyCheckMinClosingSpeed) continue;

      applyCheckResolution(state, attacker, victim, normalX, normalY, resolution.attackerState);
    }
  }
}

function isDisabled(p: PlayerState): boolean {
  // Slice 8 — benched skaters (OT wings) are off-ice; they cannot
  // participate in body checks as attacker or victim.
  return p.benched || p.action === "Stunned" || p.action === "RecoverCheck";
}

function resolveCheckCandidates(
  a: PlayerState,
  b: PlayerState,
  attackerStateByPlayerId: Map<string, AttackerState>,
): { attacker: PlayerState; victim: PlayerState; attackerState: AttackerState } | null {
  const aState = attackerStateByPlayerId.get(a.id);
  const bState = attackerStateByPlayerId.get(b.id);
  const threshold = tuning.check.bodyCheckSprintThreshold;
  const aSprinting = aState !== undefined && aState.sprint >= threshold;
  const bSprinting = bState !== undefined && bState.sprint >= threshold;
  if (aSprinting && b.hasPuck && aState !== undefined) {
    return { attacker: a, victim: b, attackerState: aState };
  }
  if (bSprinting && a.hasPuck && bState !== undefined) {
    return { attacker: b, victim: a, attackerState: bState };
  }
  return null;
}

function applyCheckResolution(
  state: MatchState,
  attacker: PlayerState,
  victim: PlayerState,
  normalX: number,
  normalY: number,
  attackerState: AttackerState,
): void {
  const mul = attackerState.bigHit ? tuning.check.bigHitMultiplier : 1.0;

  victim.hasPuck = false;
  victim.possessionQuality = 0;
  victim.action = "Stunned";
  victim.stunTicks = Math.round(tuning.check.landStunTicks * mul);
  victim.actionTimer = 0;
  victim.vel.x += normalX * tuning.check.landKnockbackSpeed * mul;
  victim.vel.y += normalY * tuning.check.landKnockbackSpeed * mul;

  attacker.action = "RecoverCheck";
  attacker.recoveryTicks = Math.round(tuning.check.attackerRecoveryAfterLandTicks * mul);
  attacker.actionTimer = 0;

  state.puck.mode = "Loose";
  state.puck.ownerId = null;
  state.puck.lastTouchedBy = attacker.id;
  state.puck.noRecaptureUntilTick = state.tick + tuning.puck.noRecaptureTicks;
  state.puck.vel.x = normalX * tuning.check.landPuckPopSpeed * mul;
  state.puck.vel.y = normalY * tuning.check.landPuckPopSpeed * mul;

  state.events.push({
    type: "CheckLanded",
    tick: state.tick,
    attacker: attacker.id,
    victim: victim.id,
    force: attackerState.sprint * mul,
  });
}
